
import React, { useState, useEffect } from 'react';
import { TabsContent } from "@/components/ui/tabs";
import { Tabs } from "@/components/ui/tabs";
import { AuditTrigger } from '@/types/audit';
import { auditTriggers } from '@/data/auditTriggers';
import { TriggerDetailView } from '@/components/compliance/TriggerDetailView';
import { AuditHeader } from '@/components/compliance/audit/AuditHeader';
import { AuditSummaryCards } from '@/components/compliance/audit/AuditSummaryCards';
import { AuditTriggerFilter } from '@/components/compliance/audit/AuditTriggerFilter';
import { AuditTriggersList } from '@/components/compliance/audit/AuditTriggersList';
import { useAuditTriggerFilter } from '@/hooks/useAuditTriggerFilter';
import { Skeleton } from "@/components/ui/skeleton";
import LazyLoad from "@/components/LazyLoad";

const AuditListSkeleton = () => (
  <div className="space-y-4 animate-pulse">
    <div className="flex justify-between mb-4">
      <Skeleton className="h-10 w-64" />
      <Skeleton className="h-10 w-32" />
    </div>
    <Skeleton className="h-20 w-full" />
    <Skeleton className="h-20 w-full" />
    <Skeleton className="h-20 w-full" />
  </div>
);

const PredictiveAuditTriggers: React.FC = () => {
  const [selectedTrigger, setSelectedTrigger] = useState<AuditTrigger | null>(null);
  const { 
    searchTerm, 
    setSearchTerm, 
    activeTab, 
    setActiveTab, 
    filteredTriggers 
  } = useAuditTriggerFilter(auditTriggers);
  
  const [isLoading, setIsLoading] = useState(true);

  // Optimize with faster initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 50); // Reduced from 100ms to 50ms for more responsive feel
    return () => clearTimeout(timer);
  }, []);
  
  if (selectedTrigger) {
    return <TriggerDetailView trigger={selectedTrigger} onClose={() => setSelectedTrigger(null)} />;
  }

  return (
    <div className="space-y-6">
      <AuditHeader />
      
      <LazyLoad delay={50}>
        <AuditSummaryCards />
      </LazyLoad>

      {isLoading ? (
        <div className="mt-6">
          <Skeleton className="h-10 w-full max-w-md mb-4" />
          <AuditListSkeleton />
        </div>
      ) : (
        <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
          <AuditTriggerFilter 
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />

          <TabsContent value={activeTab} className="mt-0">
            <LazyLoad delay={100}>
              <AuditTriggersList 
                triggers={filteredTriggers} 
                onSelectTrigger={setSelectedTrigger} 
              />
            </LazyLoad>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default PredictiveAuditTriggers;
